import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { EntryV2 } from "@/api/mappers/hsp/api/report";
import { DataTable } from "@/components";
import { formatDate } from "@/helpers/helpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface HSPMileageChartProps {
  entries: EntryV2[];
  title?: string;
  height?: number;
}

interface MileageDataPoint {
  date: string;
  mileage: number;
  formattedDate: string;
  isMileageRollback?: boolean;
}

const processMileageData = (entries: EntryV2[]): MileageDataPoint[] => {
  const sortedData = entries
    .filter((entry) => entry.mileage && entry.mileage > 0)
    .map((entry) => {
      let year = entry.displayDate;
      try {
        let date: Date;
        if (entry.displayDate.includes(".")) {
          const [day, month, yearStr] = entry.displayDate.split(".");
          date = new Date(`${yearStr}-${month}-${day}`);
        } else if (entry.displayDate.includes("-")) {
          date = new Date(entry.displayDate);
        } else {
          date = new Date(entry.displayDate);
        }

        if (!isNaN(date.getTime())) {
          year = date.getFullYear().toString();
        }
      } catch (e) {
        console.warn("Nie można sparsować daty:", entry.displayDate, e);
      }

      return {
        date: entry.displayDate,
        mileage: entry.mileage,
        formattedDate: year,
        isMileageRollback: false,
      };
    })
    .reverse();

  for (let i = 1; i < sortedData.length; i++) {
    if (sortedData[i].mileage < sortedData[i - 1].mileage) {
      sortedData[i].isMileageRollback = true;
    }
  }

  return sortedData;
};

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  dataIndex: number | null;
}

interface MileageTooltipProps {
  point: MileageDataPoint | null;
  position: { x: number; y: number };
}

const MileageTooltip: React.FC<MileageTooltipProps> = ({
  point,
  position,
}) => {
  if (!point || !point.isMileageRollback) return null;

  const { mileage, date } = point;
  const formattedDate = formatDate(date) || date;
  const formattedMileage = mileage.toLocaleString("pl-PL");

  return (
    <div
      className="pointer-events-none w-[334px] transition-all duration-200 "
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: "translate(-50%, calc(-100% - 15px))",
        zIndex: 1000,
      }}
    >
      <div className="inline-flex flex-col items-start justify-center gap-4 rounded-[15px] bg-[#101740] p-5 px-6 shadow-box">
        <div className="flex gap-3 items-center">
          <div
            className="font-medium leading-5 text-[#B5CBE0]"
            style={{ fontSize: "12px" }}
          >
            Stan licznika:
          </div>
          <div className="flex items-center justify-center rounded-lg bg-[#EE2A2A] px-[14px] py-[6px]">
            <div
              className="font-medium text-white"
              style={{ fontSize: "12px", letterSpacing: "0.3px" }}
            >
              {formattedMileage} km
            </div>
          </div>
        </div>
        <div className="w-full">
          <div
            className="mb-2 font-bold leading-5 text-white"
            style={{ fontSize: "15px" }}
          >
            Według dostępnych danych, dnia {formattedDate} stan licznika był niższy niż podczas poprzedniego pomiaru.
          </div>
          <div
            className="font-normal leading-[18px] text-[#B5CBE0]"
            style={{ fontSize: "13px" }}
          >
            Może to świadczyć o pomyłce podczas odczytu albo celowym działaniu polegającym na manipulacji przebiegiem pojazdu.
          </div>
        </div>
        <div
          className="absolute bottom-[-8px] left-1/2 h-4 w-4 rotate-45 bg-[#101740]"
          style={{ marginLeft: "-8px" }}
        />
      </div>
    </div>
  );
};

interface StandardTooltipProps {
  point: MileageDataPoint | null;
  position: { x: number; y: number };
}

const StandardTooltip: React.FC<StandardTooltipProps> = ({
  point,
  position,
}) => {
  if (!point) return null;

  const { mileage } = point;
  const formattedMileage = mileage.toLocaleString("pl-PL");

  return (
    <div
      className="transition-all duration-200 pointer-events-none"
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: "translate(-50%, calc(-100% - 15px))",
        zIndex: 999,
      }}
    >
      <div className="inline-flex flex-col items-start justify-center gap-2 rounded-[8px] bg-[#101740] py-[6px] px-[14px] shadow-box">
        <div
          className="font-semibold text-white"
          style={{ fontSize: "14px", letterSpacing: "0.4px" }}
        >
          {formattedMileage} km
        </div>
        <div
          className="absolute bottom-[-6px] left-1/2 h-3 w-3 rotate-45 bg-[#101740]"
          style={{ marginLeft: "-6px" }}
        />
      </div>
    </div>
  );
};

export const HSPMileageChart: React.FC<HSPMileageChartProps> = ({
  entries,
  height = 400,
}) => {
  const mileageData = useMemo(() => processMileageData(entries), [entries]);
  const chartRef = useRef<ChartJS<"line">>(null);
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    dataIndex: null,
  });

  const chartData = useMemo(
    () => ({
      labels: mileageData.map((point) => point.formattedDate),
      datasets: [
        {
          label: "Przebieg (km)",
          data: mileageData.map((point) => point.mileage),
          borderColor: "#B3D1FF",
          backgroundColor: "rgba(179, 209, 255, 0.1)",
          borderWidth: 2,
          pointRadius: mileageData.map((point) =>
            point.isMileageRollback ? 7 : 5
          ),
          pointHoverRadius: mileageData.map((point) =>
            point.isMileageRollback ? 9 : 7
          ),
          pointBackgroundColor: mileageData.map((point) =>
            point.isMileageRollback ? "#EE2A2A" : "#0064FF"
          ),
          pointBorderColor: mileageData.map((point) =>
            point.isMileageRollback
              ? "rgba(238, 42, 42, 0.3)"
              : "rgba(0, 100, 255, 0.3)"
          ),
          pointBorderWidth: mileageData.map((point) =>
            point.isMileageRollback ? 6 : 6
          ),
          pointStyle: "circle",
          tension: 0,
          fill: true,
        },
      ],
    }),
    [mileageData]
  );

  const handleExternalTooltip = useCallback(
    (context: { chart: ChartJS; tooltip: TooltipModel<"line"> }) => {
      const { chart, tooltip } = context;

      // Ukryj tooltip jeśli opacity jest 0
      if (tooltip.opacity === 0) {
        setTooltipState({ visible: false, x: 0, y: 0, dataIndex: null });
        return;
      }

      // Sprawdź czy są jakieś punkty danych
      if (!tooltip.dataPoints || tooltip.dataPoints.length === 0) {
        setTooltipState({ visible: false, x: 0, y: 0, dataIndex: null });
        return;
      }

      const dataIndex = tooltip.dataPoints[0].dataIndex;

      // Sprawdź czy indeks jest poprawny
      if (
        dataIndex === undefined ||
        dataIndex === null ||
        dataIndex < 0 ||
        dataIndex >= mileageData.length
      ) {
        setTooltipState({ visible: false, x: 0, y: 0, dataIndex: null });
        return;
      }

      const point = mileageData[dataIndex];

      // Pokazuj tooltip dla wszystkich punktów
      if (!point) {
        setTooltipState({ visible: false, x: 0, y: 0, dataIndex: null });
        return;
      }

      // Użyj pozycji z tooltip model zgodnie z dokumentacją Chart.js
      // Dla position: fixed używamy pozycji względem viewportu
      const position = chart.canvas.getBoundingClientRect();
      const x = position.left + tooltip.caretX;
      const y = position.top + tooltip.caretY;

      setTooltipState({
        visible: true,
        x,
        y,
        dataIndex,
      });
    },
    [mileageData]
  );

  const options: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 12,
        },
      },
      interaction: {
        mode: "point" as const,
        intersect: true,
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: handleExternalTooltip,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              family: "Inter, sans-serif",
              size: 11,
            },
            maxRotation: 0,
            minRotation: 0,
          },
          title: {
            display: false,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: "#EAECF6",
            borderDash: [5, 5],
          },
          ticks: {
            font: {
              family: "Inter, sans-serif",
              size: 11,
            },
            callback: (value) => {
              return `${(value as number).toLocaleString("pl-PL")} km`;
            },
          },
          title: {
            display: false,
          },
        },
      },
    }),
    [handleExternalTooltip]
  );

  if (!entries || entries.length === 0) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-center text-gray-500">
          Brak danych o przebiegu do wyświetlenia
        </p>
      </div>
    );
  }

  if (mileageData.length === 0) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-center text-gray-500">
          Brak zdarzeń z informacją o przebiegu
        </p>
      </div>
    );
  }

  if (mileageData.length < 2) {
    return (
      <div className="p-6 w-full bg-white">
        <div
          className="flex relative justify-center items-center mt-10"
          style={{ height: `${height}px` }}
        >
          <div className="text-center">
            <div className="mb-4 text-gray-400">
              <svg
                className="mx-auto w-16 h-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Brak danych do narysowania wykresu
            </h3>
            <p className="text-sm text-gray-500">
              {mileageData.length === 0
                ? "Nie znaleziono żadnych danych o przebiegu pojazdu."
                : "Potrzebne są co najmniej 2 punkty pomiarowe do utworzenia wykresu."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const tooltipPoint =
    tooltipState.dataIndex !== null
      ? mileageData[tooltipState.dataIndex]
      : null;
  return (
    <div className="p-6 w-full bg-white">
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .chartjs-tooltip {
          opacity: 0 !important;
          pointer-events: none !important;
          display: none !important;
        }
      `,
        }}
      />
      <div className="relative mt-10" style={{ height: `${height}px` }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
      {typeof window !== "undefined" &&
        tooltipState.visible &&
        tooltipPoint &&
        createPortal(
          tooltipPoint.isMileageRollback ? (
            <MileageTooltip
              point={tooltipPoint}
              position={{ x: tooltipState.x, y: tooltipState.y }}
            />
          ) : (
            <StandardTooltip
              point={tooltipPoint}
              position={{ x: tooltipState.x, y: tooltipState.y }}
            />
          ),
          document.body
        )}
      <div className="pt-4 mt-6 lg:mt-10">
        <div className="mt-6">
          <h4 className="mb-8 text-base font-bold text-gray-900">
            Tabela odczytów przebiegu
          </h4>

          <div className="w-full xl:w-1/2">
            <DataTable>
              {mileageData.map((point) => (
                <DataTable.Row key={point.date}>
                  <DataTable.Column width="70%">
                    <div className="flex flex-col gap-2 justify-start md:flex-row md:justify-between">
                      {formatDate(point.date) ?? point.date}
                      {point.isMileageRollback && (
                        <div className=" text-red-600 ml-auto flex max-w-full items-center gap-2 text-xs font-medium md:max-w-[50%] md:text-right">
                          Stan licznika niższy niż podczas poprzedniego pomiaru.
                          <svg
                            width={36}
                            height={36}
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="flex-grow-0 flex-shrink-0 w-9 h-9"
                            preserveAspectRatio="none"
                          >
                            <rect
                              width={36}
                              height={36}
                              rx={10}
                              fill="#F8E7E9"
                            />
                            <path
                              d="M10.8 15H25.2C25.6774 15 26.1352 15.1844 26.4728 15.5126C26.8104 15.8408 27 16.2859 27 16.75V27.25C27 27.7141 26.8104 28.1592 26.4728 28.4874C26.1352 28.8156 25.6774 29 25.2 29H10.8C10.3226 29 9.86477 28.8156 9.52721 28.4874C9.18964 28.1592 9 27.7141 9 27.25V16.75C9 16.2859 9.18964 15.8408 9.52721 15.5126C9.86477 15.1844 10.3226 15 10.8 15ZM10.8 16.75V27.25H17.1V16.75H10.8ZM25.2 27.25V16.75H24.084C24.3 17.2225 24.255 17.6863 24.255 17.7387C24.192 18.325 23.769 18.9375 23.616 19.1562L21.519 21.3875L24.507 21.37L24.516 22.4375L19.836 22.4113L19.8 21.5363C19.8 21.5363 22.545 18.71 22.68 18.4562C22.806 18.2112 23.319 16.75 22.05 16.75C20.943 16.7938 21.069 17.8875 21.069 17.8875L19.683 17.8963C19.683 17.8963 19.692 17.3187 20.025 16.75H18.9V27.25H21.222L21.213 26.4975L22.086 26.4887C22.086 26.4887 22.905 26.3488 22.914 25.57C22.95 24.695 22.185 24.695 22.05 24.695C21.933 24.695 21.087 24.7387 21.087 25.4563H19.719C19.719 25.4563 19.755 23.6538 22.05 23.6538C24.39 23.6538 24.264 25.4212 24.264 25.4212C24.264 25.4212 24.3 26.515 23.265 26.9262L23.733 27.25H25.2ZM15.228 25.5H13.878V20.425L12.258 20.915V19.8387L15.084 18.8587H15.228V25.5Z"
                              fill="#EE2A2A"
                            />
                            <path
                              d="M14.0179 13L14.0179 9.8L27 9.8L27.0301 8.192L14.0179 8.192L14.0179 5L9 9L14.0179 13Z"
                              fill="#EE2A2A"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </DataTable.Column>
                  <DataTable.Column width="30%">
                    {point.mileage.toString() + " km"}
                  </DataTable.Column>
                </DataTable.Row>
              ))}
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  );
};
